import axios, { AxiosInstance, AxiosError } from "axios";
import { error, info } from "../../utils/notification";
import config from "../../utils/config";
import storage from "../../utils/storage";
import { ILoginUserResponse } from "../../interfaces/user";
import { stringify } from "querystring";

let isNetworkErrorShown = false;
let isOffline = false; // Tracks global offline state (only set by browser events)
let lastOnlineNotificationTime = 0;
let lastOfflineNotificationTime = 0;
const NOTIFICATION_DEBOUNCE_MS = 5000; // Don't show same notification within 5 seconds

export class Api {
	protected http: AxiosInstance;

	constructor() {
		this.http = axios.create({
			baseURL: config.baseURI,
			headers: {
				"Content-Type": "application/json"
			},
			timeout: 30000 // 30 seconds timeout
		});

		this.addInterceptors();
		this.setupOnlineOfflineListeners();
	}

	private addInterceptors() {
		this.http.interceptors.request.use((config) => {
			const accessToken = storage.getItem("access_token");
			if (accessToken) {
				config.headers.authorization = accessToken;
			}
			return config;
		});

		this.http.interceptors.response.use(
			(response) => {
				// Only show "back online" if we were actually marked offline by browser event
				// and enough time has passed since last notification
				const now = Date.now();
				if (
					isOffline &&
					navigator.onLine &&
					now - lastOnlineNotificationTime > NOTIFICATION_DEBOUNCE_MS
				) {
					isOffline = false;
					isNetworkErrorShown = false;
					lastOnlineNotificationTime = now;
					info("You are back online");
				}
				return response;
			},
			(err) => {
				// Only treat as offline if browser actually reports offline status
				// API failures without response could be timeouts, CORS, or server issues
				// We should only show "offline" when the browser confirms it
				if (!err.response) {
					// Check if browser actually reports offline status
					const isActuallyOffline = !navigator.onLine;

					// Only show offline notification if browser confirms offline status
					// This prevents false offline notifications from API errors, timeouts, etc.
					if (isActuallyOffline && !isNetworkErrorShown) {
						const now = Date.now();
						if (now - lastOfflineNotificationTime > NOTIFICATION_DEBOUNCE_MS) {
							isNetworkErrorShown = true;
							isOffline = true;
							lastOfflineNotificationTime = now;
							error("You are offline. Some features may not work.");
						}
					}
					// If browser says online but we got network error, it's likely:
					// - Server is down
					// - Request timeout
					// - CORS issue
					// - Other network configuration issue
					// Don't show offline notification - these are not offline scenarios
					return Promise.reject(err);
				}

				// Reset network error flag on any response (means we're connected)
				if (isNetworkErrorShown && navigator.onLine) {
					isNetworkErrorShown = false;
				}

				// Refresh token logic
				const originalReq = err.config;
				const refreshToken = storage.getItem("refresh_token");

				if (
					err.response.status === 401 &&
					originalReq &&
					!originalReq.__isRetryRequest &&
					err.response.data.detail === "INVALID TOKEN" &&
					refreshToken
				) {
					originalReq._retry = true;
					return fetch(config.api.endpoints.refreshToken, {
						method: "POST",
						headers: {
							"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
						},
						body: stringify({ refresh_token: refreshToken })
					})
						.then((res) => res.json())
						.then((res) => {
							this.setSession(res);
							return this.http.request(originalReq);
						});
				}

				return Promise.reject(err);
			}
		);
	}

	private setupOnlineOfflineListeners() {
		window.addEventListener("offline", () => {
			const now = Date.now();
			if (!isOffline && now - lastOfflineNotificationTime > NOTIFICATION_DEBOUNCE_MS) {
				isOffline = true;
				isNetworkErrorShown = true;
				lastOfflineNotificationTime = now;
				error("You are offline. Some features may not work.");
			}
		});

		window.addEventListener("online", () => {
			const now = Date.now();
			if (isOffline && now - lastOnlineNotificationTime > NOTIFICATION_DEBOUNCE_MS) {
				isOffline = false;
				isNetworkErrorShown = false;
				lastOnlineNotificationTime = now;
				info("You are back online");
			}
		});
	}

	setSession({ access_token, refresh_token }: ILoginUserResponse) {
		storage.setItem("access_token", access_token);
		storage.setItem("refresh_token", refresh_token);
	}

	handleError(err: AxiosError | any, title?: string) {
		// Network error already handled in interceptor
		if (!err.response) return;

		switch (err.response.status) {
			case 401:
				window.location.reload();
				return error({
					title: "Session Expired",
					description: "Please refresh or log out and log back in"
				});
			case 404:
				return error("Resource doesn't exist");
			case 500:
				return; // optional: ignore or log server errors
			default:
				break;
		}

		if (err.response.data.message) {
			if (err.response.data.message === "User is not verified by admin") {
				return error(
					{
						title: "Login Error:",
						description: "Thank you for registering, please check your email for next steps"
					},
					{ type: "warning", timeout: 5000 }
				);
			}

			if (err.response.data.message === "Unauthorized access for Journey food Admin") {
				return;
			}

			return error({
				title: title || "",
				description: err.response.data.message
			});
		}

		error({
			title: title || "",
			description: err.response.data || "Something went wrong"
		});
	}
}
